import React, { createContext, useContext, useLayoutEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { AgentFilterProvider, useAgentFilters } from '../../contexts/AgentFilterContext';
import { setEntityScope } from '../../services/vendor/vendorStore';
import useLinkedSelection from '../../contexts/useLinkedSelection';

/**
 * The AI Providers module's agent / user selection (2026-10-11).
 *
 * One AgentFilterProvider for the whole /saas-ai tree, per vendor: its storage
 * key names the vendor (`ai_providers_<vendor>_filters`) and it is remounted on
 * a vendor switch (SaasAtlasWindow keys it), so one provider's agents never
 * scope another's.
 *
 * The selection is mirrored into vendorStore (setEntityScope), which
 * saasAiClient reads to append `?scope_agent=` / `?scope_user=` to every
 * request; the saas-ai service narrows lists, KPIs and aggregates to it. It is
 * mirrored only while a page that draws the pickers is open (SCOPED_PAGES), so
 * a page without them (Settings, Policy, Recommendations, Usage & ROI, the
 * security pages, the Audit Report tab) is never narrowed by a selection it
 * does not show. The store is written in a layout effect: those all run before
 * any page's data effects, so a page's first fetch after a change already
 * carries the new scope.
 *
 * Pages read the selection with useSaasEntityScope() (for their fetch deps and
 * for narrowing Atlas rows, which the Atlas API does not scope) and draw the
 * agent / user Filters in the page's filter bar (<ProviderScopeBar />).
 */

/** The /saas-ai pages that honour (and show) the selection. */
export const SCOPED_PAGES = Object.freeze([
  '/saas-ai/dashboard',
  '/saas-ai/registry',
  '/saas-ai/sources',
  '/saas-ai/conversations',
  '/saas-ai/alerts',
  '/saas-ai/users',
  '/saas-ai/intent-analysis',
  '/saas-ai/knowledge-gaps',
  '/saas-ai/compliance',
  '/saas-ai/log-explorer',
]);

/**
 * The scoped pages that read nothing of an Agent 360 / User 360 link themselves: there the link's
 * `?agent_id=<vendor>:<application>` / `?user=` becomes the selection (useLinkedSelection), so
 * the Filters bar shows it and every request carries it. Users reads `?agent=` on its own.
 */
export const LINK_PAGES = Object.freeze(['/saas-ai/compliance', '/saas-ai/log-explorer']);

/** Whether the page at `location` honours the selection (the Audit Report, /saas-ai/audit-report, does not). */
export const isScopedPage = ({ pathname = '' } = {}) => {
  const path = String(pathname).replace(/\/+$/, '');
  if (!SCOPED_PAGES.includes(path)) return false;
  return true;
};

export const storageKeyOf = (vendor) => `ai_providers_${vendor}_filters`;

const EMPTY = Object.freeze({ agents: [], users: [] });
const SaasEntityScopeContext = createContext(null);

const ScopeSync = ({ vendor, children }) => {
  const { filters } = useAgentFilters();
  const location = useLocation();
  const active = isScopedPage(location);
  useLinkedSelection(vendor, { enabled: active && LINK_PAGES.includes(String(location.pathname).replace(/\/+$/, '')) });
  const agents = filters?.agents || EMPTY.agents;
  const users = filters?.users || EMPTY.users;
  const key = JSON.stringify([vendor, agents, users]);

  useLayoutEffect(() => {
    setEntityScope(active ? { vendor, agents, users } : null);
  }, [active, key]);  // eslint-disable-line react-hooks/exhaustive-deps -- key covers vendor, agents, users
  // Leaving the module (or switching vendor) drops the scope.
  useLayoutEffect(() => () => setEntityScope(null), []);

  // What the open page sees is exactly what its requests carry: nothing on a page
  // that does not honour the selection.
  const value = useMemo(() => (active ? {
    vendor, agents, users, active, key, any: agents.length > 0 || users.length > 0, mounted: true,
  } : {
    vendor, agents: EMPTY.agents, users: EMPTY.users, active, key: '', any: false, mounted: true,
  }), [key, active]);  // eslint-disable-line react-hooks/exhaustive-deps
  return <SaasEntityScopeContext.Provider value={value}>{children}</SaasEntityScopeContext.Provider>;
};

export const SaasEntityScopeProvider = ({ vendor, children }) => (
  <AgentFilterProvider key={vendor} storageKey={storageKeyOf(vendor)}>
    <ScopeSync vendor={vendor}>{children}</ScopeSync>
  </AgentFilterProvider>
);

const NO_SCOPE = Object.freeze({
  vendor: null, agents: EMPTY.agents, users: EMPTY.users, active: false, key: '', any: false, mounted: false,
});

/**
 * The selection the open page's requests carry: `{ agents, users, key, any,
 * active }` (`key` changes whenever the selection does: put it in a fetch's
 * deps). Empty on a page that does not honour it (`active: false`) and outside
 * the module (a page rendered on its own, `mounted: false`).
 */
export function useSaasEntityScope() {
  return useContext(SaasEntityScopeContext) || NO_SCOPE;
}

export default SaasEntityScopeProvider;
